const isFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseDateValue = (rawValue) => {
  if (!rawValue) return null;
  if (rawValue instanceof Date) return rawValue;
  if (typeof rawValue === 'number') {
    return new Date(rawValue < 1e12 ? rawValue * 1000 : rawValue);
  }
  const numeric = Number(rawValue);
  if (!Number.isNaN(numeric) && rawValue !== '') {
    return new Date(numeric < 1e12 ? numeric * 1000 : numeric);
  }
  const parsed = new Date(rawValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeTeam = (team = {}, fallbackName = 'Team') => {
  const name = team?.name ?? team?.teamName ?? fallbackName;
  const score =
    isFiniteNumber(
      team?.score ?? team?.points ?? team?.total ?? team?.sets ?? null
    ) ?? null;

  return {
    id: team?.id ?? team?.uuid ?? name,
    name,
    score,
    city: team?.city ?? null,
    isWinner: team?.isWinner ?? null,
  };
};

const determineWinners = (home, away) => {
  if (home.isWinner != null || away.isWinner != null) {
    return {
      home: Boolean(home.isWinner),
      away: Boolean(away.isWinner),
    };
  }
  if (home.score == null || away.score == null) {
    return { home: null, away: null };
  }
  if (home.score === away.score) {
    return { home: null, away: null };
  }
  return {
    home: home.score > away.score,
    away: away.score > home.score,
  };
};

const buildMatchId = ({
  rawMatch,
  homeTeam,
  awayTeam,
  completedAt,
  scheduledAt,
}) => {
  const fallbackDate = completedAt ?? scheduledAt;
  return (
    rawMatch?.id ??
    rawMatch?.matchId ??
    rawMatch?.match_id ??
    rawMatch?.uuid ??
    [homeTeam?.name, awayTeam?.name, fallbackDate?.toISOString()]
      .filter(Boolean)
      .join(':')
  );
};

export const normalizeMatch = (match = {}) => {
  const center = match?.bowlingCenter ?? {};
  const homeTeam = normalizeTeam(match?.homeTeam, 'Home team');
  const awayTeam = normalizeTeam(match?.awayTeam, 'Away team');
  const scheduledAt = parseDateValue(match?.scheduledAt ?? match?.date ?? null);
  const completedAt = parseDateValue(match?.completedAt ?? match?.date ?? null);
  const winners = determineWinners(homeTeam, awayTeam);
  const id = buildMatchId({
    rawMatch: match,
    homeTeam,
    awayTeam,
    completedAt,
    scheduledAt,
  });

  return {
    id,
    matchId: match?.matchId ?? id,
    leagueId: match?.leagueId ?? null,
    leagueName: match?.leagueName ?? 'Swedish League',
    centerUuid: match?.centerUuid ?? center?.uuid ?? null,
    centerName: match?.centerName ?? center?.name ?? '',
    city: match?.city ?? center?.city ?? null,
    status: match?.status ?? null,
    scheduledAt,
    completedAt,
    homeTeam: { ...homeTeam, isWinner: winners.home },
    awayTeam: { ...awayTeam, isWinner: winners.away },
  };
};

export const normalizeWebsocketMatch = (liveMatch = {}) => {
  if (!liveMatch) return null;

  // For websocket payload: uuid is the center UUID, league_id is the league ID
  const centerUuid = liveMatch?.uuid ?? null;
  const leagueId = liveMatch?.league_id ?? null;

  const matchIdentifier =
    liveMatch?.match_id ??
    liveMatch?.matchId ??
    liveMatch?.id ??
    [
      centerUuid,
      leagueId,
      liveMatch?.home_team?.name,
      liveMatch?.away_team?.name,
    ]
      .filter(Boolean)
      .join(':');

  const mappedPayload = {
    id: matchIdentifier,
    matchId: matchIdentifier,
    leagueId,
    leagueName: 'Swedish League',
    centerUuid,
    centerName: liveMatch?.bowling_name ?? '',
    bowlingCenter: {
      uuid: centerUuid,
      name: liveMatch?.bowling_name ?? null,
    },
    scheduledAt: liveMatch?.started_at ?? null,
    status: 'ongoing',
    homeTeam: liveMatch?.home_team ?? null,
    awayTeam: liveMatch?.away_team ?? null,
  };

  return normalizeMatch(mappedPayload);
};
